import React from "react";
import {CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import config, { getApiUrl } from "./config";
import './OverlayChart.css';
import toast from "react-hot-toast";
import {ReactComponent as CloseIcon} from "./close.svg";
import {DateTime} from "luxon";
import authService from "./AuthService";

type OverlayChartProps = {
    endpointId: string,
    applianceId: string,
    serviceUri: string,
    dataPoint: string,
    unit: string,
    from: string,
    to: string,
    aggregateFunction: string,
    windowPeriod: string
    closeCallback: () => void
}

type OverlayChartState = {
    from: string,
    to: string,
    windowPeriod: string,
    aggregateFunction: string,
    data: any[]
    ticks: number
}

type TimeSeriesPoint = {
    time: number,
    value: number
}

const CustomTooltip = ({active, payload, label}: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="OverlayChart-tooltip">
                <p className="OverlayChart-tooltip-time">{DateTime.fromMillis(label || 0).toLocaleString(DateTime.DATETIME_SHORT)}</p>
                <p className="OverlayChart-tooltip-value">{payload[0].value.toFixed(3)}</p>
            </div>
        );
    }

    return null;
};

class OverlayChart extends React.Component<OverlayChartProps, OverlayChartState> {
    static defaultProps = {
        from: "-24h",
        to: "now()",
        windowPeriod: "10m",
        aggregateFunction: "mean"
    }

    constructor(props: OverlayChartProps) {
        super(props);
        this.state = {
            from: this.props.from,
            to: this.props.to,
            windowPeriod: this.props.windowPeriod,
            aggregateFunction: this.props.aggregateFunction,
            data: [],
            ticks: 7
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    private fetchData = () => {
        const params = new URLSearchParams();
        params.append("from", this.state.from);
        params.append("to", this.state.to);
        params.append("aggregateInterval", this.state.windowPeriod);
        params.append("aggregateFunction", this.state.aggregateFunction);
        console.log(params.toString());

        fetch(getApiUrl() + "/v1/endpoint/" + this.props.endpointId + "/series/" + this.props.applianceId + "/" + this.props.serviceUri + "/" + this.props.dataPoint + "?" + params.toString(), {
            headers: [
                ["Authorization", authService.authHeader()],
                ["Accept", "application/json"]
            ]
        }).then(response => {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            return response.json() as Promise<Array<TimeSeriesPoint>>;
        }).then(data => {
            this.setState({data: data.map(point => ({_time: point.time, value: point.value}))});
        }).catch(error => {
            toast.error(error.message);
        });
    }

    render() {
        return (
            <div className="OverlayChart">
                <div className="OverlayChart-content">
                    <div className="OverlayChart-controls">
                        <button className={"OverlayChart-interval" + (this.state.from === "-1h" ? " active" : "")}
                                onClick={() => this.setState({from: "-1h", windowPeriod: "1m"}, this.fetchData)}>Last
                            hour
                        </button>
                        <button className={"OverlayChart-interval" + (this.state.from === "-24h" ? " active" : "")}
                                onClick={() => this.setState({from: "-24h", windowPeriod: "10m"}, this.fetchData)}>Last
                            24 hours
                        </button>
                        <button className={"OverlayChart-interval" + (this.state.from === "-7d" ? " active" : "")}
                                onClick={() => this.setState({from: "-7d", windowPeriod: "1h"}, this.fetchData)}>Last
                            week
                        </button>
                        <button className={"OverlayChart-interval" + (this.state.from === "--31d" ? " active" : "")}
                                onClick={() => this.setState({from: "-31d", windowPeriod: "6h"}, this.fetchData)}>Last
                            31 days
                        </button>
                        <span className="OverlayChart-spacer"/>
                        <button
                            className={"OverlayChart-interval" + (this.state.aggregateFunction === "mean" ? " active" : "")}
                            onClick={() => this.setState({aggregateFunction: "mean"}, this.fetchData)}>mean
                        </button>
                        <button
                            className={"OverlayChart-interval" + (this.state.aggregateFunction === "max" ? " active" : "")}
                            onClick={() => this.setState({aggregateFunction: "max"}, this.fetchData)}>max
                        </button>
                        <button
                            className={"OverlayChart-interval" + (this.state.aggregateFunction === "min" ? " active" : "")}
                            onClick={() => this.setState({aggregateFunction: "min"}, this.fetchData)}>min
                        </button>
                        <span className="OverlayChart-spacer"/>
                        <button className="OverlayChart-close" onClick={this.props.closeCallback}><CloseIcon/></button>
                    </div>
                    <ResponsiveContainer>
                        <LineChart data={this.state.data}>
                            <XAxis type="number" scale="utc"
                                   domain={[this.state.data.length > 0 ? this.state.data[0].time : 'auto', this.state.data.length > 0 ? this.state.data[this.state.data.length - 1].time : 'auto']}
                                   dataKey="_time" tickCount={this.state.ticks}
                                   tickFormatter={(seconds: number) => DateTime.fromSeconds(seconds).toRelative() || ""}
                                   interval="equidistantPreserveStart"/>
                            <YAxis/>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <Tooltip content={<CustomTooltip/>}/>
                            <Line isAnimationActive={false} type="monotone" dataKey="value" stroke="#8bb63a"
                                  strokeWidth={2} dot={false}/>
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }
}

export default OverlayChart;